import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  PageHeader,
} from "@pushpress/pushpress-ui";
import {
  useGetCalendarItemQuery,
  useInvitePeopleToEventMutation,
} from "../../../../apis/calendarApi.hooks";
import { defaultToastOptions } from "../../../../utils/misc";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useState, MouseEvent } from "react";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";

export interface EventInvitationBannerProps {
  show: boolean;
  eventId: string;
  userUuids: string[];
}

const MAX_INVITES = 100;

interface InviteConfirmModalProps {
  eventName: string;
  isSubmitting: boolean;
  selectedPeopleCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
}

function InviteConfirmDialog({
  eventName,
  selectedPeopleCount,
  onSubmit,
  isSubmitting,
  ...props
}: InviteConfirmModalProps) {
  return (
    <Dialog {...props}>
      <DialogContent className="tw-max-w-md">
        <DialogHeader className="tw-space-y-6 tw-text-left">
          <DialogTitle className="tw-text-lg tw-text-center">
            Invite to {eventName}
          </DialogTitle>
          <DialogDescription className="tw-text-sm tw-pb-4">
            An email invite will be sent to the {selectedPeopleCount}{" "}
            {selectedPeopleCount === 1 ? "person" : "people"} you selected. This
            will allow them to sign-up for {eventName}.
          </DialogDescription>
        </DialogHeader>
        <Button
          className="tw-w-full"
          onClick={onSubmit}
          isSubmitting={isSubmitting}
        >
          Send Invitation Email
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function EventInvitationBanner({
  show,
  userUuids,
  eventId,
}: EventInvitationBannerProps) {
  const history = useHistory();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { trackEvent } = useAnalytics();

  const { data: event, error } = useGetCalendarItemQuery(eventId, {
    enabled: show,
  });

  const { mutate, isLoading: isSubmitting } = useInvitePeopleToEventMutation({
    onError: (err) => {
      toast.error(
        err?.message ??
          "Something went wrong inviting people. Please contact support if the issue persists",
        defaultToastOptions,
      );
    },
    onSuccess: (_, { userUuids, calendarItemUuid }) => {
      trackEvent("EventInvitation_Created", {
        userUuids,
        eventUuid: calendarItemUuid,
      });
      toast.success(
        `${userUuids.length} ${userUuids.length === 1 ? "person" : "people"} invited successfully`,
        defaultToastOptions,
      );
    },
    onSettled: () => {
      setConfirmDialogOpen(false);
    },
  });

  const invite = async () => {
    // TODO: batch this in the background
    if (userUuids.length > MAX_INVITES) {
      toast.error(
        `You can only invite up to ${MAX_INVITES} people at a time.`,
        defaultToastOptions,
      );
      return;
    }
    mutate({ calendarItemUuid: eventId, userUuids });
  };

  if (error) {
    toast.error("Unable to fetch event", defaultToastOptions);
    return null;
  }

  // TODO: add loading state (DEV-816)
  if (!show || !event) {
    return null;
  }

  return (
    <PageHeader
      subtitle="Invite people to"
      title={event.title}
      className="tw-w-full tw-bg-white tw-z-50 tw-py-5"
      onBack={() => history.push(`/schedule/${eventId}`)}
    >
      <p className="tw-text-neutral-600 tw-mr-4">
        {userUuids.length} {userUuids.length === 1 ? "person" : "people"}{" "}
        selected
      </p>
      <Button
        disabled={!userUuids.length}
        variant="default"
        size="lg"
        onClick={() => {
          setConfirmDialogOpen(true);
        }}
      >
        Invite People
      </Button>
      <InviteConfirmDialog
        isSubmitting={isSubmitting}
        eventName={event.title}
        selectedPeopleCount={userUuids.length}
        onSubmit={invite}
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
      />
    </PageHeader>
  );
}
